import type { Mutation, QueryClient } from '@tanstack/react-query';
import { onlineManager } from '@tanstack/react-query';
import { emitToast } from '~/components/feedback';
import { ApiRequestError } from './client';
import { renderApiError } from './error-renderer';

/**
 * F2 — сторож инварианта I1: ни одна мутация не умирает молча.
 *
 * F1 убрал причину подвисания (детектор онлайна больше не верит одиночному событию,
 * мутации ходят с `networkMode: 'always'`), но сам КЛАСС «оптимистик применён, запрос
 * не ушёл, пользователь ничего не знает» этим не закрыт: любая будущая мутация с
 * `networkMode: 'online'`, любой сторонний код, дёрнувший `onlineManager.setOnline(false)`,
 * вернут его. Сторож ловит состояние, а не причину.
 *
 * Что делает при `isPaused` дольше отсрочки:
 *  1) детектор офлайна разошёлся с браузером (`navigator.onLine === true`) — чинит
 *     детектор; мутация уходит сама, экран и сервер сходятся;
 *  2) сети действительно нет — снимает мутацию с учёта (иначе она выстрелит через час
 *     поверх изменившегося экрана) и запускает её собственный путь отката: `onError`
 *     каждой мутации задач возвращает кэш и показывает адресный тост с «Повторить».
 *     У мутации без `onError` тост эмитится здесь.
 *
 * Чего сторож НЕ ловит (§6.2): мутацию, умершую ПОСЛЕ старта fetch — сетевой таймаут
 * без ответа. Там состояние `isPending`, а не `isPaused`, и лечится оно таймаутом
 * запроса, а не этим сторожем.
 */
export const PAUSED_MUTATION_GRACE_MS = 5_000;

/** Код ошибки отката: рендерится через общий `renderApiError` (i18n, ru+en). */
export const OFFLINE_PAUSED_CODE = 'network.offline_paused';

type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;

export function installPausedMutationWatcher(
  queryClient: QueryClient,
  graceMs: number = PAUSED_MUTATION_GRACE_MS,
): () => void {
  const cache = queryClient.getMutationCache();
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  const disarm = (id: number): void => {
    const timer = timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(id);
    }
  };

  const abandon = (mutation: AnyMutation): void => {
    cache.remove(mutation);
    const error = new ApiRequestError(0, { code: OFFLINE_PAUSED_CODE });
    const onError = mutation.options.onError as
      | ((error: unknown, variables: unknown, onMutateResult: unknown) => unknown)
      | undefined;
    if (onError) {
      try {
        void onError(error, mutation.state.variables, mutation.state.context);
        return;
      } catch {
        // Откат мутации сам упал — ниже общий тост, чтобы молчания всё равно не было.
      }
    }
    const rendered = renderApiError(error);
    emitToast({ variant: 'error', title: rendered.title, description: rendered.body });
  };

  const arm = (mutation: AnyMutation): void => {
    if (timers.has(mutation.mutationId)) return;
    timers.set(
      mutation.mutationId,
      setTimeout(() => {
        timers.delete(mutation.mutationId);
        if (!mutation.state.isPaused) return;
        const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
        if (browserOnline && !onlineManager.isOnline()) {
          // Самолечение: браузер online, детектор — нет. Возврат снимает паузу со
          // ВСЕЙ очереди, а не только с этой мутации.
          onlineManager.setOnline(true);
          return;
        }
        abandon(mutation);
      }, graceMs),
    );
  };

  const unsubscribe = cache.subscribe((event) => {
    const mutation = (event as { mutation?: AnyMutation }).mutation;
    if (!mutation) return;
    if (mutation.state.isPaused) arm(mutation);
    else disarm(mutation.mutationId);
  });

  return () => {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    unsubscribe();
  };
}
